import React, { useState, useRef } from 'react'
import toast from 'react-hot-toast'
import { useApp } from '../context/AppContext'
import { Icon, useIsMobile } from '../shared'
import { Card, Btn, Badge, Segmented, TableWrap, ExportMenu } from '../shared/components'
import {
  importParticipantsCSV,
  downloadParticipantImportTemplate,
  downloadParticipantImportTemplateXlsx,
  downloadParticipantImportTemplatePdf,
  previewParticipantImport,
  createParticipant,
  listClasses,
} from '../api/programs.api'

// ─── Pemetaan hasil pratinjau server ─────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Mengubah baris hasil pratinjau server jadi bentuk yang dipakai langkah review.
//
// Yang mengurai berkasnya server; yang diperiksa di sini hanya hal-hal yang
// memang urusan tampilan — email tidak sah, nama/perusahaan kosong, duplikat —
// supaya operator bisa membetulkannya sebelum berkasnya benar-benar diimpor.
function keBarisPratinjau(rows) {
  const seen = new Set()
  return rows.map((r, i) => {
    const email = (r.email || '').trim()
    const fullName = (r.full_name || '').trim()
    const company = (r.client_company || '').trim()

    let issue = null
    let tone = 'danger'
    if (!email || !EMAIL_RE.test(email)) issue = 'Invalid email format'
    else if (!fullName) issue = 'Missing name'
    else if (!company) issue = 'Missing company'
    else if (seen.has(email.toLowerCase())) { issue = 'Duplicate row'; tone = 'warning' }

    if (email) seen.add(email.toLowerCase())

    return {
      id: `r${i}`,
      rowNum: r._row ?? i + 2,
      email, fullName, company,
      nauticalCertNo: r.nautical_cert_no || '',
      hublaCertNo: r.hubla_cert_no || '',
      dob: r.date_of_birth || '',
      pob: r.place_of_birth || '',
      issue, tone, skip: false,
    }
  })
}
// ─── Stepper ─────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 'upload',    n: 1, label: 'Upload CSV'  },
  { id: 'review',    n: 2, label: 'Review & fix' },
  { id: 'configure', n: 3, label: 'Configure'   },
  { id: 'confirm',   n: 4, label: 'Confirm'     },
]

function Stepper({ step, onBack }) {
  const idx = STEPS.findIndex(s => s.id === step)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 28, flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => {
        const done = i < idx
        const cur  = i === idx
        return (
          <React.Fragment key={s.id}>
            <button
              onClick={() => done && onBack(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', background: 'transparent',
                border: 'none', cursor: done ? 'pointer' : 'default',
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: done ? 'var(--success)' : cur ? 'var(--program-color)' : 'var(--surface-3)',
                color: (done || cur) ? '#fff' : 'var(--text-3)',
                display: 'grid', placeItems: 'center',
                fontSize: 11, fontWeight: 500,
              }}>
                {done ? <Icon name="check" size={10} /> : s.n}
              </span>
              <span style={{
                fontSize: 13,
                color: cur ? 'var(--text)' : done ? 'var(--text-2)' : 'var(--text-4)',
                fontWeight: cur ? 500 : 400,
              }}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div style={{ width: 32, height: 1, background: i < idx ? 'var(--success)' : 'var(--border)', flexShrink: 0 }} />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

// ─── Upload Step ─────────────────────────────────────────────────────────────
function UploadStep({ programId, onParsed }) {
  const isMobile = useIsMobile()
  // Maritime credential columns are shown only to institutions that run
  // maritime training (see Tenant.maritime_fields).
  const { branding } = useApp()
  const maritime = branding?.maritime_fields === true
  const [uploadState, setUploadState] = useState('idle')
  const [uploadPct,   setUploadPct]   = useState(0)
  const [fileName,    setFileName]    = useState('')
  const fileRef = useRef()
  const [downloadingTpl, setDownloadingTpl] = useState(null)

  const handleFile = async (file) => {
    if (!file) return
    setFileName(file.name)
    setUploadState('parsing')
    setUploadPct(0)

    // Animate progress ring
    let p = 0
    const iv = setInterval(() => {
      p += 5
      setUploadPct(Math.min(95, p))
      if (p >= 95) clearInterval(iv)
    }, 30)

    try {
      // Diurai di SERVER, bukan di peramban. Dulu berkasnya diurai dua kali —
      // sekali di sini untuk pratinjau, sekali lagi di server saat diunggah —
      // oleh dua parser berbeda yang bisa tidak sepakat. Parser di sini bahkan
      // memakai split(',') tanpa penanganan tanda kutip, jadi nama seperti
      // "Budi, S.T." sudah merusak pratinjau, dan .xlsx tidak terbaca sama
      // sekali. Sekarang yang ditampilkan adalah apa yang benar-benar dibaca
      // server saat impor nanti.
      const hasil = await previewParticipantImport(programId, file)
      clearInterval(iv)
      if (hasil.error) {
        setUploadState('idle')
        toast.error(hasil.error)
        return
      }
      const rows = keBarisPratinjau(hasil.rows || [])
      setUploadPct(100)
      setUploadState('done')
      setTimeout(() => onParsed(rows, file), 600)
    } catch (e) {
      clearInterval(iv)
      setUploadState('idle')
      toast.error(e?.response?.data?.error || 'Failed to read file')
    }
  }

  // kind datang dari ExportMenu sebagai "template-<format>"; ekstensinya
  // diturunkan dari akhirannya, pola yang sama dipakai layar Laporan.
  const handleDownloadTemplate = async (kind) => {
    setDownloadingTpl(kind)
    try {
      const fn = {
        'template-csv': downloadParticipantImportTemplate,
        'template-xlsx': downloadParticipantImportTemplateXlsx,
        'template-pdf': downloadParticipantImportTemplatePdf,
      }[kind]
      if (!fn) return
      const blob = await fn(programId)
      const ext = kind.split('-').pop()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `template-impor-peserta.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      // PDF dibuat Chromium, yang bisa saja tidak tersedia — itu 503, bukan
      // kegagalan yang perlu ditampilkan sebagai galat umum.
      if (e?.response?.status === 503) toast.error('Ekspor PDF sedang tidak tersedia')
      else toast.error(e?.response?.data?.error || 'Failed to download template')
    } finally {
      setDownloadingTpl(null)
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 20 }}>
      {/* minWidth:0 prevents the flex child from overflowing its 1fr grid track
          when a nested table-scroll has min-width:560px on mobile (TD-6 fix) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); if (uploadState === 'idle') setUploadState('dragging') }}
          onDragLeave={() => { if (uploadState === 'dragging') setUploadState('idle') }}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
          onClick={() => uploadState === 'idle' && fileRef.current?.click()}
          style={{
            border: `2px dashed ${uploadState === 'dragging' ? 'var(--program-color)' : 'var(--border-strong)'}`,
            borderRadius: 16,
            background: uploadState === 'dragging' ? 'var(--program-color-soft)' : 'var(--surface)',
            padding: '56px 32px',
            textAlign: 'center',
            transition: 'all 0.15s',
            cursor: uploadState === 'idle' ? 'pointer' : 'default',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; if (f) handleFile(f) }}
          />

          {uploadState === 'idle' && (
            <>
              <div style={{
                width: 72, height: 72, borderRadius: 18,
                background: 'var(--program-color-soft)',
                color: 'var(--program-color-text)',
                display: 'grid', placeItems: 'center',
                margin: '0 auto 20px',
              }}>
                <Icon name="upload" size={28} />
              </div>
              <div className="h2">Drop your CSV here</div>
              <div className="muted mt-2" style={{ fontSize: 14 }}>
                or <span style={{ color: 'var(--program-color-text)', fontWeight: 500 }}>browse to upload</span>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                .CSV or .XLSX · max 10 MB · isi template lalu unggah kembali
              </div>
            </>
          )}

          {uploadState === 'dragging' && (
            <>
              <div style={{ fontSize: 56, marginBottom: 16 }}>📂</div>
              <div className="h2" style={{ color: 'var(--program-color-text)' }}>Release to upload</div>
            </>
          )}

          {(uploadState === 'parsing' || uploadState === 'done') && (
            <>
              <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 20px' }}>
                <svg viewBox="0 0 80 80" style={{ position: 'absolute', inset: 0 }}>
                  <circle cx="40" cy="40" r="34" fill="none" stroke="var(--border-subtle)" strokeWidth="5" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke={uploadState === 'done' ? 'var(--success)' : 'var(--program-color)'}
                    strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(uploadPct / 100) * 213} 213`}
                    transform="rotate(-90 40 40)"
                    style={{ transition: 'stroke-dasharray 0.08s linear' }}
                  />
                </svg>
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'grid', placeItems: 'center',
                  fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)',
                  color: uploadState === 'done' ? 'var(--success)' : 'var(--program-color-text)',
                }}>
                  {uploadState === 'done' ? <Icon name="check" size={28} /> : `${uploadPct}%`}
                </div>
              </div>
              <div className="h3">{uploadState === 'done' ? 'File uploaded!' : 'Parsing file…'}</div>
              <div className="muted mt-1" style={{ fontSize: 13 }}>{fileName}</div>
            </>
          )}
        </div>

        {/* CSV format guide */}
        <Card title="CSV column format" eyebrow="Required columns marked *">
          <TableWrap>
            <table className="tbl">
              <thead>
                <tr><th>Column</th><th>Required</th><th>Format</th><th>Example</th></tr>
              </thead>
              <tbody>
                {[
                  { col: 'email',              req: true,  fmt: 'Valid email',   ex: 'andre@pertamina.id'  },
                  { col: 'full_name',           req: true,  fmt: 'Text',          ex: 'Andre Wijaya'        },
                  { col: 'company',             req: false, fmt: 'Text',          ex: 'Pertamina Shipping'  },
                  // Maritime credentials only make sense for maritime
                  // institutions; hidden for everyone else so the guide does
                  // not ask a factory for a nautical certificate number.
                  ...(maritime ? [
                    { col: 'nautical_cert_no',  req: false, fmt: 'Text',          ex: 'ANT-II 0892341'      },
                    { col: 'hubla_cert_no',     req: false, fmt: 'Text',          ex: 'BST 224501'          },
                  ] : []),
                  { col: 'date_of_birth',       req: false, fmt: '24 Juli 1998',  ex: '24 Juli 1998'        },
                  { col: 'place_of_birth',      req: false, fmt: 'Text',          ex: 'Surabaya'            },
                  { col: 'phone',               req: false, fmt: 'Text',          ex: '081234567890'        },
                ].map(r => (
                  <tr key={r.col}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {r.col}{r.req && <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>}
                    </td>
                    <td><Badge tone={r.req ? 'danger' : 'default'}>{r.req ? 'Required' : 'Optional'}</Badge></td>
                    <td className="muted" style={{ fontSize: 12 }}>{r.fmt}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)' }}>{r.ex}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      </div>

      {/* Right rail */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title="Tips for a clean import">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            {[
              { t: 'Use the template',  d: 'Download and fill the CSV template to avoid column mismatches.' },
              { t: 'UTF-8 encoding',    d: 'Save your file as UTF-8 to preserve Indonesian characters.' },
              { t: 'One row per person', d: 'Duplicate email rows will be detected and flagged.' },
              { t: 'Max 500 rows',      d: 'For larger cohorts, split into multiple imports.' },
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                  background: 'var(--success-soft)', color: 'var(--success)',
                  display: 'grid', placeItems: 'center',
                }}>
                  <Icon name="check" size={12} />
                </span>
                <div>
                  <div style={{ fontWeight: 500 }}>{tip.t}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{tip.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
            <ExportMenu
              formats={['csv', 'xlsx', 'pdf']}
              kindPrefix="template"
              busyKind={downloadingTpl}
              onSelect={handleDownloadTemplate}
              lang="en"
              label="Download template"
            />
          </div>
        </Card>
      </div>
    </div>
  )
}

// ─── Review + Configure Step ─────────────────────────────────────────────────
function ReviewStep({ program, rows, fileName, rawFile, onReplace, onRowsChange }) {
  const isMobile = useIsMobile()
  const [issueFilter, setIssueFilter] = useState('all')
  const [mode,        setMode]        = useState('PUBLIC')
  const [classes,     setClasses]     = useState([])
  const [classId,     setClassId]     = useState('')
  const [trainingDate, setTrainingDate] = useState('')
  const [trainingType, setTrainingType] = useState('initial')
  const [sendQrEmail, setSendQrEmail] = useState(true)
  const [showModal,   setShowModal]   = useState(false)
  const [editId,      setEditId]      = useState(null)
  const [editValues,  setEditValues]  = useState({})

  // Daftar kelas program ini. Gagal memuatnya BUKAN alasan menggagalkan
  // impor — tanpa kelas, impor tetap berjalan seperti sebelum fitur Kelas
  // ada, dan barisnya masuk sebagai enrolmen tanpa kelas.
  React.useEffect(() => {
    if (!program?.id) return
    let batal = false
    listClasses(program.id)
      .then(d => { if (!batal) setClasses(Array.isArray(d) ? d : d?.classes || []) })
      .catch(() => { if (!batal) setClasses([]) })
    return () => { batal = true }
  }, [program?.id])

  // Kelas inhouse membawa perusahaannya sendiri untuk seluruh peserta, jadi
  // mode di sini mengikutinya alih-alih dibiarkan bertentangan.
  const kelasTerpilih = classes.find(k => k.id === classId) || null
  React.useEffect(() => {
    if (kelasTerpilih?.mode) setMode(kelasTerpilih.mode)
  }, [kelasTerpilih?.mode])

  const valid   = rows.filter(r => !r.issue || r.skip)
  const issues  = rows.filter(r => r.issue && !r.skip)
  const willEnroll = rows.filter(r => !r.issue && !r.skip).length

  const skipRow = (id) => onRowsChange(rows.map(r => r.id === id ? { ...r, skip: true } : r))

  const startEdit = (row) => {
    setEditId(row.id)
    setEditValues({ email: row.email, fullName: row.fullName, company: row.company })
  }

  const saveEdit = (id) => {
    onRowsChange(rows.map(r => {
      if (r.id !== id) return r
      const updated = { ...r, ...editValues }
      // re-validate
      let issue = null, tone = 'danger'
      if (!updated.email || !EMAIL_RE.test(updated.email)) issue = 'Invalid email format'
      else if (!updated.fullName) issue = 'Missing name'
      else if (!updated.company) issue = 'Missing company'
      return { ...updated, issue, tone, skip: false }
    }))
    setEditId(null)
  }

  const filteredIssues = issues.filter(r => {
    if (issueFilter === 'err')  return r.tone === 'danger'
    if (issueFilter === 'warn') return r.tone === 'warning'
    return true
  })

  const validPreview = rows.filter(r => !r.issue && !r.skip).slice(0, 5)
  const moreCount    = willEnroll - validPreview.length

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 16 }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Upload summary */}
          <Card
            title={fileName}
            eyebrow="Step 1 · uploaded"
            action={<Btn variant="ghost" size="sm" icon={<Icon name="close" size={14} />} onClick={onReplace}>Replace</Btn>}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 8, flexShrink: 0,
                background: 'var(--program-color-soft)', color: 'var(--program-color-deep)',
                display: 'grid', placeItems: 'center',
              }}>
                <Icon name="doc" size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                  <span><b>{rows.length}</b> rows detected</span>
                  <span className="muted">·</span>
                  <span style={{ color: 'var(--success)' }}>{rows.length - issues.length} valid</span>
                  {issues.length > 0 && <>
                    <span className="muted">·</span>
                    <span style={{ color: 'var(--danger)' }}>{issues.length} with issues</span>
                  </>}
                </div>
                <div style={{ height: 6, background: 'var(--surface-3)', borderRadius: 3, marginTop: 8, display: 'flex', overflow: 'hidden' }}>
                  <div style={{ flex: rows.length - issues.length, background: 'var(--success)' }} />
                  <div style={{ flex: issues.length, background: 'var(--danger)' }} />
                </div>
              </div>
            </div>
          </Card>

          {/* Issues table */}
          {issues.length > 0 && (
            <Card
              title="Issues to resolve"
              eyebrow={`${issues.length} rows`}
              action={
                <Segmented
                  value={issueFilter}
                  onChange={setIssueFilter}
                  options={[
                    { value: 'all',  label: 'All' },
                    { value: 'err',  label: 'Errors' },
                    { value: 'warn', label: 'Warnings' },
                  ]}
                />
              }
              noPad
            >
              <TableWrap>
                <table className="tbl">
                  <thead>
                    <tr><th>#</th><th>Email</th><th>Name</th><th>Company</th><th>Issue</th><th /></tr>
                  </thead>
                  <tbody>
                    {filteredIssues.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', fontSize: 12 }}>
                          {String(r.rowNum).padStart(3, '0')}
                        </td>
                        {editId === r.id ? (
                          <>
                            <td><input className="input" style={{ width: 160, fontSize: 12 }} value={editValues.email}    onChange={e => setEditValues(v => ({ ...v, email:    e.target.value }))} /></td>
                            <td><input className="input" style={{ width: 120, fontSize: 12 }} value={editValues.fullName} onChange={e => setEditValues(v => ({ ...v, fullName: e.target.value }))} /></td>
                            <td><input className="input" style={{ width: 140, fontSize: 12 }} value={editValues.company}  onChange={e => setEditValues(v => ({ ...v, company:  e.target.value }))} /></td>
                            <td><Badge tone={r.tone}>{r.issue}</Badge></td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Btn size="sm" variant="primary" onClick={() => saveEdit(r.id)}>Save</Btn>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.email || <span className="faint">—</span>}</td>
                            <td>{r.fullName || <span className="faint">—</span>}</td>
                            <td>{r.company  || <span className="faint">—</span>}</td>
                            <td><Badge tone={r.tone}>{r.issue}</Badge></td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Btn size="sm" variant="ghost" onClick={() => startEdit(r)}>Edit</Btn>
                              <Btn size="sm" variant="ghost" onClick={() => skipRow(r.id)}>Skip</Btn>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </Card>
          )}

          {/* Valid rows preview */}
          <Card title="Valid rows preview" eyebrow={`${willEnroll} will be enrolled`} noPad>
            <TableWrap>
              <table className="tbl">
                <thead>
                  <tr><th>Email</th><th>Name</th><th>Company</th><th>Nautical cert</th><th>Hubla cert</th></tr>
                </thead>
                <tbody>
                  {validPreview.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.email}</td>
                      <td>{r.fullName}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{r.company}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.nauticalCertNo || <span className="faint">—</span>}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.hublaCertNo    || <span className="faint">—</span>}</td>
                    </tr>
                  ))}
                  {moreCount > 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 14, color: 'var(--text-3)', fontSize: 12 }}>
                        + {moreCount} more valid rows
                      </td>
                    </tr>
                  )}
                  {willEnroll === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)', fontSize: 13 }}>
                        No valid rows yet — fix issues above or upload a different file.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableWrap>
          </Card>
        </div>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Enrollment configuration">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div className="field-label" style={{ marginBottom: 6 }}>Class</div>
                <select
                  className="select"
                  value={classId}
                  onChange={e => setClassId(e.target.value)}
                  disabled={classes.length === 0}
                >
                  <option value="">— no class (whole program) —</option>
                  {classes.map(k => (
                    <option key={k.id} value={k.id}>
                      {k.name}{k.mode === 'INHOUSE' && k.client_company ? ` · ${k.client_company}` : ''}
                    </option>
                  ))}
                </select>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  {classes.length === 0
                    ? 'This program has no classes yet — participants will be enrolled without one.'
                    : kelasTerpilih
                      ? 'Participants join this class. Attendance is counted against its sessions only.'
                      : 'Without a class, attendance is counted against every session in the program.'}
                </div>
              </div>
              <div>
                <div className="field-label" style={{ marginBottom: 8 }}>Training mode</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { v: 'PUBLIC',  l: 'Public',   sub: 'Mixed companies'      },
                    { v: 'INHOUSE', l: 'In-house',  sub: 'Single company cohort' },
                  ].map(o => (
                    <button
                      key={o.v}
                      onClick={() => setMode(o.v)}
                      style={{
                        padding: '10px 12px', textAlign: 'left', cursor: 'pointer',
                        border: `1px solid ${mode === o.v ? 'var(--program-color)' : 'var(--border)'}`,
                        background: mode === o.v ? 'var(--program-color-soft)' : 'var(--surface)',
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: 13, color: mode === o.v ? 'var(--program-color-text)' : 'var(--text)' }}>{o.l}</div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{o.sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="field-label" style={{ marginBottom: 6 }}>Training date</div>
                <input
                  className="input"
                  placeholder="e.g. 23 May 2026 → 6 Jun 2026"
                  value={trainingDate}
                  onChange={e => setTrainingDate(e.target.value)}
                />
              </div>

              <div>
                <div className="field-label" style={{ marginBottom: 6 }}>Training type</div>
                <select className="select" value={trainingType} onChange={e => setTrainingType(e.target.value)}>
                  <option value="initial">Initial certification</option>
                  <option value="refresher">Refresher</option>
                </select>
              </div>

              <div>
                <div className="field-label" style={{ marginBottom: 6 }}>Welcome email</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sendQrEmail} onChange={e => setSendQrEmail(e.target.checked)} />
                  Notify participants with login QR
                </label>
              </div>
            </div>
          </Card>

          <Card title="Summary">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              {[
                { l: 'Program',      v: program?.code?.toUpperCase() },
                { l: 'Class',        v: kelasTerpilih?.name || '— none —' },
                { l: 'Mode',         v: mode === 'INHOUSE' ? 'In-house' : 'Public' },
                { l: 'Rows in file', v: String(rows.length) },
                { l: 'Will enroll',  v: String(willEnroll),       color: 'var(--success)' },
                { l: 'Skipped',      v: String(rows.length - willEnroll), color: 'var(--danger)' },
              ].map(row => (
                <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="muted">{row.l}</span>
                  <span style={{ fontWeight: 500, color: row.color || 'var(--text)' }}>{row.v}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 12 }}>
                <Btn
                  variant="primary"
                  block
                  icon={<Icon name="upload" size={14} />}
                  disabled={willEnroll === 0}
                  onClick={() => setShowModal(true)}
                >
                  Enroll {willEnroll} participants
                </Btn>
                {sendQrEmail && (
                  <div className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 6 }}>
                    Login QR codes will be generated and emailed.
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {showModal && (
        <EnrollModal
          program={program}
          mode={mode}
          trainingDate={trainingDate}
          classId={classId}
          className_={kelasTerpilih?.name || null}
          sendQrEmail={sendQrEmail}
          willEnroll={willEnroll}
          skippedCount={rows.length - willEnroll}
          rawFile={rawFile}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}

// ─── Enrollment Modal ─────────────────────────────────────────────────────────
const ENROLL_TASKS = [
  'Validating participant records',
  'Creating user accounts',
  'Enrolling participants',
  'Generating login QR codes',
  'Sending welcome emails',
]

function EnrollModal({ program, mode, trainingDate, classId, className_, sendQrEmail, willEnroll, skippedCount, rawFile, onClose }) {
  const { navigate } = useApp()
  const [phase,      setPhase]    = useState('confirm')
  const [progress,   setProgress] = useState(0)
  const [taskIdx,    setTaskIdx]  = useState(0)
  const [result,     setResult]   = useState(null)

  const startProcessing = async () => {
    setPhase('processing')
    setProgress(0)

    // Simulate progress while API runs
    let p = 0
    const iv = setInterval(() => {
      p = Math.min(p + 1.5, 90)
      setProgress(p)
      setTaskIdx(Math.min(ENROLL_TASKS.length - 1, Math.floor((p / 100) * ENROLL_TASKS.length)))
    }, 50)

    try {
      const res = await importParticipantsCSV(program.id, rawFile, { sendQrEmail, classId })
      clearInterval(iv)
      setProgress(100)
      setResult(res)
      setTimeout(() => setPhase('done'), 400)
    } catch (e) {
      clearInterval(iv)
      toast.error(e?.response?.data?.error || 'Enrollment failed')
      setPhase('confirm')
      setProgress(0)
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(11,20,23,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={e => { if (phase !== 'processing' && e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--surface)',
        borderRadius: 20,
        boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 540,
        overflow: 'hidden',
        border: '1px solid var(--border-subtle)',
      }}>

        {/* ── Confirm ── */}
        {phase === 'confirm' && (
          <>
            <div style={{ padding: '28px 28px 0' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: 'var(--program-color-soft)', color: 'var(--program-color-deep)',
                  display: 'grid', placeItems: 'center',
                }}>
                  <Icon name="users" size={20} />
                </div>
                <div>
                  <div className="h2">Confirm enrollment</div>
                  <div className="muted mt-1" style={{ fontSize: 13 }}>Review the summary before running the batch import.</div>
                </div>
              </div>

              <div style={{
                marginTop: 24,
                background: 'var(--surface-2)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 12, overflow: 'hidden',
              }}>
                {[
                  { l: 'Program',         v: `${program?.code?.toUpperCase()} · ${program?.name}` },
                  { l: 'Class',           v: className_ || '— none —' },
                  { l: 'Training mode',   v: mode === 'INHOUSE' ? 'In-house (single company)' : 'Public (mixed companies)' },
                  { l: 'Training dates',  v: trainingDate || '—' },
                  { l: 'Will enroll',     v: String(willEnroll),    color: 'var(--success)' },
                  { l: 'Skipped / issues', v: `${skippedCount} rows`, color: skippedCount > 0 ? 'var(--danger)' : undefined },
                  { l: 'Welcome email',   v: sendQrEmail ? 'Yes — with login QR code' : 'No' },
                ].map((r, i, arr) => (
                  <div
                    key={r.l}
                    style={{
                      display: 'flex', justifyContent: 'space-between',
                      padding: '11px 16px', fontSize: 13,
                      borderBottom: i < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    }}
                  >
                    <span className="muted">{r.l}</span>
                    <span style={{ fontWeight: 500, color: r.color || 'var(--text)' }}>{r.v}</span>
                  </div>
                ))}
              </div>

              {skippedCount > 0 && (
                <div style={{
                  marginTop: 14, padding: '10px 14px',
                  background: 'var(--amber-soft)',
                  border: '1px solid var(--amber)',
                  borderRadius: 10, fontSize: 12,
                  color: 'var(--text-2)',
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <Icon name="clock" size={14} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <b style={{ color: 'var(--amber)' }}>{skippedCount} rows skipped.</b>{' '}
                    Fix them in the CSV and re-import, or enroll them manually afterward.
                  </div>
                </div>
              )}
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '20px 28px',
              borderTop: '1px solid var(--border-subtle)',
              marginTop: 20,
            }}>
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              <Btn variant="primary" size="lg" icon={<Icon name="upload" size={16} />} onClick={startProcessing}>
                Yes, enroll {willEnroll} participants
              </Btn>
            </div>
          </>
        )}

        {/* ── Processing ── */}
        {phase === 'processing' && (
          <div style={{ padding: '48px 36px', textAlign: 'center' }}>
            <style>{`
              @keyframes enroll-expand { 0% { transform: scale(0.85); opacity: 0.15; } 100% { transform: scale(1.15); opacity: 0; } }
              @keyframes enroll-spin   { to { transform: rotate(360deg); } }
            `}</style>
            <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto' }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  position: 'absolute', inset: -(i * 14),
                  borderRadius: '50%',
                  border: '1.5px solid var(--program-color)',
                  opacity: 0.15 - i * 0.04,
                  animation: `enroll-expand ${1.4 + i * 0.3}s ease-out ${i * 0.2}s infinite`,
                }} />
              ))}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: '3px solid var(--border-subtle)',
                borderTopColor: 'var(--program-color)',
                animation: 'enroll-spin 1s linear infinite',
              }} />
              <div style={{
                position: 'absolute', inset: 4, borderRadius: '50%',
                background: 'var(--program-color-soft)',
                display: 'grid', placeItems: 'center',
              }}>
                <Icon name="upload" size={28} style={{ color: 'var(--program-color-text)' }} />
              </div>
            </div>

            <div className="h2" style={{ marginTop: 28 }}>Processing enrollment…</div>
            <div className="muted mt-2" style={{ fontSize: 13 }}>Please don't close this window.</div>

            <div style={{ marginTop: 28, textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{ENROLL_TASKS[taskIdx]}</span>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--program-color-text)', fontWeight: 600 }}>{Math.round(progress)}%</span>
              </div>
              <div style={{ height: 8, background: 'var(--surface-3)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${progress}%`,
                  background: 'linear-gradient(90deg, var(--program-color-deep), var(--program-color))',
                  borderRadius: 99,
                  transition: 'width 0.1s linear',
                  boxShadow: '0 0 10px rgba(var(--program-color-rgb), 0.4)',
                }} />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 24, textAlign: 'left' }}>
              {ENROLL_TASKS.map((t, i) => {
                const tp   = (progress / 100) * ENROLL_TASKS.length
                const done = tp > i + 1
                const act  = tp > i && !done
                return (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12 }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                      background: done ? 'var(--success)' : act ? 'var(--program-color-soft)' : 'var(--surface-3)',
                      color: done ? '#fff' : 'var(--text-3)',
                      display: 'grid', placeItems: 'center',
                      border: act ? '1.5px solid var(--program-color)' : 'none',
                    }}>
                      {done ? <Icon name="check" size={10} /> : act ? (
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--program-color)' }} />
                      ) : null}
                    </span>
                    <span style={{
                      color: done ? 'var(--success)' : act ? 'var(--text)' : 'var(--text-4)',
                      fontWeight: act ? 500 : 400,
                    }}>{t}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Done ── */}
        {phase === 'done' && (
          <div style={{ padding: '48px 36px', textAlign: 'center' }}>
            <div style={{ position: 'relative', width: 96, height: 96, margin: '0 auto' }}>
              <svg viewBox="0 0 96 96" style={{ position: 'absolute', inset: 0 }}>
                <circle cx="48" cy="48" r="44" fill="var(--success-soft)" />
                <circle cx="48" cy="48" r="44" fill="none" stroke="var(--success)" strokeWidth="2" />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <Icon name="check" size={40} style={{ color: 'var(--success)' }} />
              </div>
            </div>

            <div className="h1" style={{ marginTop: 24, fontSize: 28 }}>
              {result?.enrolled ?? willEnroll} participants enrolled!
            </div>
            <div className="muted" style={{ fontSize: 14, maxWidth: 380, margin: '10px auto 0', lineHeight: 1.6 }}>
              {sendQrEmail
                ? 'Login QR codes have been generated and welcome emails are being dispatched.'
                : 'Participants have been enrolled successfully.'}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { n: result?.enrolled ?? willEnroll,  l: 'Enrolled',            bg: 'var(--success-soft)',       c: 'var(--success)'            },
                { n: result?.created  ?? 0,           l: 'New accounts',        bg: 'var(--surface-2)',          c: 'var(--text)',    border: true },
                { n: result?.errors?.length ?? 0,     l: 'Rows skipped',        bg: 'var(--amber-soft)',         c: 'var(--amber)'              },
              ].map(chip => (
                <div key={chip.l} style={{
                  padding: '10px 18px', borderRadius: 10, fontSize: 13, textAlign: 'center',
                  background: chip.bg,
                  border: chip.border ? '1px solid var(--border-subtle)' : 'none',
                }}>
                  <div style={{ fontWeight: 600, color: chip.c, fontSize: 22 }}>{chip.n}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{chip.l}</div>
                </div>
              ))}
            </div>

            {result?.quotaWarning && (
              <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--amber-soft)', border: '1px solid var(--amber)', borderRadius: 10, fontSize: 12, color: 'var(--text-2)' }}>
                <Icon name="alert" size={14} style={{ color: 'var(--amber)', marginRight: 6 }} />
                {result.quotaWarning}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 28, justifyContent: 'center' }}>
              <Btn variant="secondary" onClick={onClose}>Start new import</Btn>
              <Btn variant="primary" onClick={() => { onClose(); navigate('users') }}>Go to participants</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main screen ─────────────────────────────────────────────────────────────
function AdminEnrollment({ program }) {
  const [step,       setStep]       = useState('upload')
  const [parsedRows, setParsedRows] = useState([])
  const [rawFile,    setRawFile]    = useState(null)
  const [fileName,   setFileName]   = useState('')

  const handleParsed = (rows, file) => {
    setParsedRows(rows)
    setRawFile(file)
    setFileName(file.name)
    setStep('review')
  }

  const handleReplace = () => {
    setParsedRows([])
    setRawFile(null)
    setFileName('')
    setStep('upload')
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{program?.code?.toUpperCase()} · Batch enrollment</div>
          <h1 className="h1" style={{ marginTop: 6 }}>Import participants</h1>
          <div className="muted mt-1">Upload a CSV file to enroll multiple participants at once.</div>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <Stepper step={step} onBack={setStep} />

        {step === 'upload' && (
          <UploadStep programId={program?.id} onParsed={handleParsed} />
        )}

        {step !== 'upload' && (
          <ReviewStep
            program={program}
            rows={parsedRows}
            fileName={fileName}
            rawFile={rawFile}
            onReplace={handleReplace}
            onRowsChange={setParsedRows}
          />
        )}
      </div>
    </div>
  )
}

export default function EnrollmentScreen() {
  const { currentProgram } = useApp()
  return <AdminEnrollment program={currentProgram} />
}
